import type { IConfirmDialogProps } from 'components/ConfirmDialog';
import { State } from 'utils/state';
import { shouldShowAlert, getAlertConfig, interceptAlert } from 'utils/alerts';

export type DialogStatus = 'idle' | 'opening' | 'open' | 'closing';

export interface IConfirmDialogState extends Omit<IConfirmDialogProps, 'isOpen'> {
  isOpen?: boolean;
  isBeingRendered?: boolean;
  status?: DialogStatus;
}

const defaults: Partial<IConfirmDialogState> = {
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  hideCancel: false,
};

/**
 * How long an opened-but-never-claimed confirm dialog is allowed to wait before it is
 * force-resolved as cancelled. This is a safety net for the case where every mounted
 * web-component host is hidden/unmounted before any of them claims the dialog for
 * rendering (see AVS-4351) — without this, the dialog's promise would never settle and
 * whoever is awaiting it (e.g. `share()`) would hang forever.
 */
export const UNCLAIMED_DIALOG_SETTLE_MS = 5000;

// Handle for the pending "unclaimed dialog" settle timer, if one is armed. Typed instead of
// `any` per project convention.
let unclaimedSettleTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Clears any pending unclaimed-dialog settle timer. Safe to call even when no timer is armed.
 */
function clearUnclaimedSettleTimer() {
  if (unclaimedSettleTimer !== null) {
    clearTimeout(unclaimedSettleTimer);
    unclaimedSettleTimer = null;
  }
}

export const confirmDialogState = new State<IConfirmDialogState>({
  ...defaults,
  isOpen: false,
  isBeingRendered: false,
  status: 'idle',
  title: '',
  onConfirm: () => {},
  onCancel: () => {},
});

/**
 * Shows a confirmation dialog from anywhere in the app.
 * Only one dialog can be shown at a time (singleton pattern).
 *
 * @example
 * Basic usage:
 * ```tsx
 * import { showConfirmDialog } from 'state/confirm-dialog';
 *
 * const confirmed = await showConfirmDialog({
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?',
 *   confirmText: 'Delete',
 *   cancelText: 'Cancel',
 * });
 *
 * if (confirmed) {
 *   console.log('User confirmed!');
 * } else {
 *   console.log('User cancelled!');
 * }
 * ```
 *
 * @example
 * With custom content (form fields):
 * ```tsx
 * const confirmed = await showConfirmDialog({
 *   title: 'Enter Details',
 *   message: 'Please provide additional information:',
 *   children: (
 *     <div>
 *       <input type="text" placeholder="Name" className="..." />
 *       <textarea placeholder="Notes" className="..." />
 *     </div>
 *   ),
 * });
 * ```
 *
 * @param dialogProps - The dialog configuration (without onConfirm/onCancel callbacks)
 * @returns Promise<boolean> - true if confirmed, false if cancelled
 */
export function showConfirmDialog(
  dialogProps: Omit<IConfirmDialogProps, 'isOpen' | 'onConfirm' | 'onCancel'>
): Promise<boolean> {
  // Ensure dialogId is defined, generate unique ID if missing
  const dialogId = dialogProps.dialogId || `dialog_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  // Check alert filter — suppress if filtered out
  const suppressed = dialogProps.dialogId ? !shouldShowAlert(dialogProps.dialogId, getAlertConfig()?.dialogs) : false;

  // If a dialog is already active and this one isn't suppressed, reject early
  // (suppressed dialogs auto-confirm without needing UI, so the guard doesn't apply)
  const currentState = confirmDialogState.get();
  if (!suppressed && currentState.status !== 'idle') {
    if (currentState.isOpen && !currentState.isBeingRendered) {
      // Nobody ever claimed this dialog for rendering (e.g. every host was hidden when it was
      // requested) — it can never resolve on its own, so it would otherwise wedge every dialog
      // requested after it forever. Resolve it as cancelled and let the new request proceed.
      console.warn('[ConfirmDialog] Resetting a stale, never-rendered dialog to make way for a new request');
      currentState.onCancel?.();
    } else {
      console.warn('[ConfirmDialog] Dialog already active, rejecting new request');
      return Promise.resolve(false);
    }
  }

  // Notify interceptor (runtime re-emits as event)
  const proceed = interceptAlert({
    kind: 'dialog',
    suppressed,
    dialogId,
    title: dialogProps.title,
    message: dialogProps.message,
    confirmText: dialogProps.confirmText,
    cancelText: dialogProps.cancelText,
  });

  // Interceptor prevented → auto-cancel
  if (!proceed) {
    return Promise.resolve(false);
  }

  // Alert filter suppressed → auto-confirm (existing behavior)
  if (suppressed) {
    return Promise.resolve(true);
  }

  confirmDialogState.setValues({ status: 'opening' });

  return new Promise<boolean>((resolve) => {
    confirmDialogState.setValues({
      ...defaults,
      ...dialogProps,
      dialogId,
      onConfirm: () => {
        hideConfirmDialog();
        resolve(true);
      },
      onCancel: () => {
        hideConfirmDialog();
        resolve(false);
      },
      isOpen: true,
      status: 'open',
    });

    // Bounded safety net: if nobody ever claims this dialog for rendering (e.g. every
    // mounted web-component host is hidden/unmounted before claiming it), it can never
    // resolve on its own and would otherwise hang the caller forever (AVS-4351). A dialog
    // that IS claimed legitimately waits on a human indefinitely, so this only settles the
    // unclaimed case.
    unclaimedSettleTimer = setTimeout(() => {
      const latestState = confirmDialogState.get();
      const isStillOpenAndUnclaimed =
        latestState.dialogId === dialogId && latestState.isOpen === true && latestState.isBeingRendered === false;

      if (isStillOpenAndUnclaimed) {
        console.warn('[ConfirmDialog] Resolving an unclaimed dialog as cancelled after the settle timeout elapsed');
        hideConfirmDialog();
        resolve(false);
      }

      unclaimedSettleTimer = null;
    }, UNCLAIMED_DIALOG_SETTLE_MS);
  });
}

/**
 * Hides the currently displayed confirmation dialog and clears all state.
 */
export function hideConfirmDialog() {
  clearUnclaimedSettleTimer();
  confirmDialogState.setValues({
    ...defaults,
    isOpen: false,
    isBeingRendered: false,
    status: 'idle',
    title: '',
    message: undefined,
    children: undefined,
    dialogId: undefined,
    preferredClaimant: undefined,
    hideCancel: false,
    onConfirm: () => {},
    onCancel: () => {},
  });
}

/**
 * Resets the confirmation dialog state completely.
 */
export function resetConfirmDialogState() {
  clearUnclaimedSettleTimer();
  confirmDialogState.setValues(
    {
      ...defaults,
      isOpen: false,
      isBeingRendered: false,
      status: 'idle',
      title: '',
      hideCancel: false,
      onConfirm: () => {},
      onCancel: () => {},
    },
    { reset: true }
  );
}

/**
 * Claims the dialog for rendering. Returns true if successfully claimed.
 * This prevents multiple instances of the dialog from rendering simultaneously.
 */
export function claimConfirmDialogRendering(): boolean {
  const currentState = confirmDialogState.get();
  if (currentState.isOpen && !currentState.isBeingRendered) {
    confirmDialogState.setValues({ isBeingRendered: true });
    return true;
  }
  return false;
}

/**
 * Releases a rendering claim without hiding the dialog — for a host that claimed it and then
 * unmounted (e.g. an integrator removes `<e3d-sharing-dashboard>`) before the human confirmed
 * or cancelled it (AVS-4351). Without this, `isBeingRendered` would stay `true` forever: no
 * other host could claim the dialog, and the unclaimed-dialog settle timeout above only settles
 * a dialog that was never claimed, not one whose claimant disappeared. A no-op if the dialog has
 * already been hidden/settled by other means.
 */
export function releaseConfirmDialogRendering(): void {
  const currentState = confirmDialogState.get();
  if (currentState.isOpen && currentState.isBeingRendered) {
    confirmDialogState.setValues({ isBeingRendered: false });
  }
}

// Reset the status to idle when the dialog is hidden
confirmDialogState.on('change', (changedKeys, _changedValues, _oldValues, _values, currentState) => {
  if (changedKeys.includes('isOpen') && currentState.isOpen === false) {
    confirmDialogState.setValues({ isBeingRendered: false, status: 'idle' });
  }
});

export default confirmDialogState;
